
import { useEffect, useRef } from 'react';

interface ComboHeroProps {
  lang: 'en' | 'ja';
}

export default function ComboHero({ lang }: ComboHeroProps) {
  const parallaxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef.current) {
        parallaxRef.current.style.transform = `translateY(${window.scrollY * 0.3}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleWhatsApp = () => {
    const msg = lang === 'en'
      ? "Hi! I'm interested in booking the Tokyo Night Combo Experience (Izakaya + Karaoke). Could you share availability and details? Thank you!"
      : "こんにちは！Tokyo Night Combo Experience（居酒屋＋カラオケ）の予約に興味があります。空き状況と詳細を教えていただけますか？";
    window.open(`https://wa.me/819092895522?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const t = {
    en: {
      badge: 'One Night · Two Experiences',
      title: 'Experience Tokyo Night\nin One Evening',
      sub: 'Izakaya + Karaoke · The Ultimate Tokyo Night Out',
      desc: 'Start with authentic local izakaya vibes, then sing your heart out at a Japanese karaoke box — all with a friendly local guide by your side.',
      cta: 'Book via WhatsApp',
      scroll: 'Scroll to explore',
    },
    ja: {
      badge: '一夜で二つの体験',
      title: '東京の夜を\n一晩で体験する',
      sub: '居酒屋 ＋ カラオケ · 最高の東京ナイトアウト',
      desc: '地元の居酒屋でリラックスした後、カラオケボックスで思いっきり歌う。地元の友人と一緒に、本物の東京の夜を楽しもう。',
      cta: 'WhatsAppで予約',
      scroll: 'スクロールして詳細を見る',
    },
  }[lang];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background */}
      <div ref={parallaxRef} className="absolute inset-0 scale-110">
        <img
          src="https://public.readdy.ai/ai/img_res/4342992393cc6fb65850072c7d86e3b4.jpg"
          alt="Tokyo Night Combo Experience"
          className="w-full h-full object-cover object-center"
        />
      </div>
      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/80"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-amber-900/30 via-transparent to-pink-900/30"></div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-4xl mx-auto px-6 py-24 text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-5 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-8">
          <i className="ri-moon-fill text-amber-400 text-base"></i>
          <span className="text-white text-sm font-medium tracking-wide">{t.badge}</span>
          <i className="ri-music-2-fill text-pink-400 text-base"></i>
        </div>

        {/* Title */}
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-5 leading-tight whitespace-pre-line">
          {t.title}
        </h1>

        {/* Sub */}
        <p className="text-xl md:text-2xl font-semibold mb-6" style={{ color: '#fbbf24' }}>
          {t.sub}
        </p>

        {/* Desc */}
        <p className="text-base md:text-lg text-gray-200 mb-10 max-w-2xl mx-auto leading-relaxed">
          {t.desc}
        </p>

        {/* Two icons */}
        <div className="flex items-center justify-center gap-6 mb-10">
          <div className="flex flex-col items-center gap-2">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-amber-500/20 border border-amber-400/40">
              <i className="ri-beer-line text-amber-400 text-2xl"></i>
            </div>
            <span className="text-amber-200 text-xs font-medium">Izakaya</span>
          </div>
          <div className="w-8 h-px bg-white/30 relative">
            <i className="ri-add-line text-white/60 absolute -top-2.5 left-1/2 -translate-x-1/2 text-sm"></i>
          </div>
          <div className="flex flex-col items-center gap-2">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-pink-500/20 border border-pink-400/40">
              <i className="ri-mic-fill text-pink-400 text-2xl"></i>
            </div>
            <span className="text-pink-200 text-xs font-medium">Karaoke</span>
          </div>
          <div className="w-8 h-px bg-white/30 relative">
            <i className="ri-equal-line text-white/60 absolute -top-2.5 left-1/2 -translate-x-1/2 text-sm"></i>
          </div>
          <div className="flex flex-col items-center gap-2">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-rose-500/20 border border-rose-400/40">
              <i className="ri-heart-fill text-rose-400 text-2xl"></i>
            </div>
            <span className="text-rose-200 text-xs font-medium">Best Night</span>
          </div>
        </div>

        {/* CTA */}
        <button
          onClick={handleWhatsApp}
          className="inline-flex items-center gap-3 px-10 py-4 bg-green-500 hover:bg-green-600 text-white text-lg font-bold rounded-full shadow-2xl hover:shadow-green-500/40 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-whatsapp-line text-2xl"></i>
          {t.cta}
        </button>

        {/* Scroll hint */}
        <div className="mt-14 flex flex-col items-center gap-2 opacity-60">
          <span className="text-white text-xs tracking-widest uppercase">{t.scroll}</span>
          <i className="ri-arrow-down-line text-white text-xl animate-bounce"></i>
        </div>
      </div>
    </section>
  );
}
